import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from '@/alerts/index';
import { llmStaypal } from '@/api/requests/llm/llm';
import { useServiceToken } from '@/auth/index';
import { useAppConfigContext } from '@/context/AppConfig';
import { ResponseError, convertKeysToCamelCase } from '@/utils/index';

interface LLMStaypalHookProps {
  shouldSuspendExecution?: boolean;
}

export const useLLMStaypal = (llmStaypalHookData: LLMStaypalHookProps) => {
  const { shouldSuspendExecution } = llmStaypalHookData;
  const { data: tokenData } = useServiceToken('llm');
  const { enqueueSnackbar } = useSnackbar();
  const { logError } = useAppConfigContext();
  const useLLMStaypalMaxRetries = 2;

  const handleSmartSearchError = (error: ResponseError) => {
    if (error.response?.status === 429) {
      // OpenAI quota or rate limited exceeded error
      logError('staypal: rate limit error', error);
    } else {
      logError('staypal: generation failed', error);
    }

    enqueueSnackbar('Staypal response generation failed', {
      variant: 'error',
      autoHideDuration: 3000,
    });
  };

  const fetchStaypalResponse = async ({
    userInput,
    sessionId,
  }: {
    userInput: string;
    sessionId: string;
  }) => {
    if (shouldSuspendExecution) return null;

    const response = await llmStaypal.createStaypalRequest(tokenData.token, {
      search: userInput,
      context_id: sessionId,
    });

    return Promise.resolve(convertKeysToCamelCase(response));
  };

  const staypalMutation = useMutation(fetchStaypalResponse, {
    retry: (failureCount, error: ResponseError) => {
      // Do not retry if OpenAI quota or rate limited exceeded
      if (error?.response?.status === 429) {
        return false;
      }

      const maxRetries = useLLMStaypalMaxRetries || 1;
      return failureCount < maxRetries;
    },
    onError: (error: ResponseError) => {
      handleSmartSearchError(error);
    },
  });

  return staypalMutation;
};
